import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PageTitle } from "@/components/SettingsPanel";
import { CurrencyCards, DateRange, EntriesList, type LedgerEntry, type StatementData } from "@/components/Statement";
import { LedgerEntryDialog } from "@/components/LedgerEntryDialog";
import { StatementPdfButton } from "@/components/StatementPdf";
import { ReasonDialog } from "@/components/BookingStatus";
import { MonthlySummaries } from "@/components/MonthlySummaries";
import { FeeComparison } from "@/components/FeeComparison";
import { fmtMoney } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";
import { useLive } from "@/lib/live";

interface Acc { account_id: string; business_name: string; status: string; balances: Record<string, string> }

export default function AdminFinance() {
  const { t } = usePrefs();
  const [data, setData] = useState<{ accounts: Acc[]; totals: Record<string, Record<string, string>> } | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [st, setSt] = useState<StatementData | null>(null);
  const [dialog, setDialog] = useState<"payment" | "debit_adj" | null>(null);
  const [rev, setRev] = useState<LedgerEntry | null>(null);
  const resend = async (e: LedgerEntry) => {
    try {
      const { data: r } = await api.post(`/admin/ledger/${e.entry_id}/resend-receipt`);
      toast[r.status === "sent" ? "success" : "warning"](t(`rc.email.${r.status}`));
      refresh();
    } catch (err) {
      toast.error(errorText(err, t));
    }
  };
  const loadAll = () => api.get("/admin/finance").then((r) => setData(r.data)).catch(() => {});
  const loadSt = useCallback(() => {
    if (!sel) return;
    const q = new URLSearchParams(Object.entries(range).filter(([, v]) => v)).toString();
    api.get(`/admin/accounts/${sel}/statement${q ? `?${q}` : ""}`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [sel, range]);
  useLive(() => { loadAll(); loadSt(); });
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadSt(); }, [loadSt]);
  const refresh = () => { loadAll(); loadSt(); };
  const me = useAuth().user as User;
  const reverse = async (reason: string) => {
    try {
      await api.post(`/admin/ledger/${rev!.entry_id}/reverse`, { reason });
      toast.success(t("b7.saved"));
      setRev(null);
      refresh();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  if (!["super", "finance"].includes(me.admin_role || "super")) return <Navigate to="/admin/dashboard" replace />;
  return (
    <div data-testid="admin-finance-page">
      <PageTitle title={t("b7.financeTitle")} sub={t("b7.financeSub")} />
      <div className="flex flex-wrap gap-3 mb-6">
        {Object.entries(data?.totals || {}).map(([c, v]) => (
          <div key={c} data-testid={`finance-total-${c}`} className="rounded-3xl bg-foreground text-background px-5 py-4 min-w-[220px]">
            <p className="text-xs opacity-70">{t("b7.outstanding")} · {c}</p>
            <p className="font-display text-2xl font-black"><bdi>{fmtMoney({ amount: v.closing, currency: c } as any)}</bdi></p>
            <p className="text-xs opacity-70">{t("b7.collected")}: <bdi>{fmtMoney({ amount: v.payments, currency: c } as any)}</bdi> · {t("b7.waived")}: <bdi>{fmtMoney({ amount: v.exemptions, currency: c } as any)}</bdi></p>
          </div>
        ))}
      </div>
      <MonthlySummaries />
      <FeeComparison />
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className="space-y-2" data-testid="finance-accounts">
          <p className="text-xs font-bold text-muted-foreground">{t("b7.accounts")}</p>
          {data?.accounts.map((a) => (
            <button key={a.account_id} onClick={() => setSel(a.account_id)} data-testid={`finance-account-${a.account_id}`} aria-pressed={sel === a.account_id}
              className={`w-full text-start rounded-2xl border p-3 transition-[background-color] ${sel === a.account_id ? "border-primary bg-primary/5" : "bg-card hover:bg-accent"}`}>
              <p className="font-bold text-sm">{a.business_name}</p>
              <p className="text-xs text-muted-foreground">{Object.entries(a.balances).map(([c, v]) => fmtMoney({ amount: v, currency: c } as any)).join(" · ") || "—"}</p>
            </button>
          ))}
        </div>
        <div>
          {!sel ? <p className="text-muted-foreground py-10">{t("b7.pickAccount")}</p> : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-2">
                <DateRange onApply={(from, to) => setRange({ from, to })} />
                <div className="flex flex-wrap items-end gap-2 mb-4">
                  <StatementPdfButton fileId={sel} endpoint={`/admin/accounts/${sel}/statement`} name={data?.accounts.find((a) => a.account_id === sel)?.business_name || sel} />
                  <button onClick={() => setDialog("payment")} data-testid="finance-add-payment" className="h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold">{t("b7.addPayment")}</button>
                  <button onClick={() => setDialog("debit_adj")} data-testid="finance-add-adjust" className="h-10 px-4 rounded-full border text-sm font-bold">{t("b7.addAdjust")}</button>
                </div>
              </div>
              {st && <><CurrencyCards data={st} /><EntriesList entries={st.entries} onReverse={setRev} onResend={resend} name={data?.accounts.find((a) => a.account_id === sel)?.business_name || ""} /></>}
            </>
          )}
        </div>
      </div>
      {dialog && sel && <LedgerEntryDialog accountId={sel} initialKind={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} />}
      {rev && <ReasonDialog testid="reverse-dialog" title={t("b7.reverseTitle")} onClose={() => setRev(null)} onSubmit={reverse} />}
    </div>
  );
}
